import React, { useState, useEffect, useMemo } from "react";
import ProjectType from "./projectType";
import ProjectDetails from "./projectDetails";
import FinalReview from "./finalReview";
import AllTemplatesEnhanced from "./allTemplatesEnhanced";

const PROJECT_STATE_KEY_PREFIX = "adperfect_project_wizard_state";

export default function ImportBrands({
  setProjectButtonVisible,
  setStep,
  step,
  setImportProject,
  brandDetails,
  projectList
}) {
  // Extract project ID from URL
  const currentProjectId = useMemo(() => {
    const urlParts = window.location.href.split("/");
    // URL structure: /brand/{brandId}/project/{projectId}
    const projectIndex = urlParts.findIndex((part) => part === "project");

    // If "project" is not in URL, this is a new project (no ID yet)
    if (projectIndex === -1) {
      return null;
    }

    const projectIdIndex = projectIndex + 1;
    return urlParts[projectIdIndex] || null;
  }, []);

  // Generate localStorage key with project ID for isolation
  const PROJECT_STATE_KEY = useMemo(() => {
    if (currentProjectId) {
      return `${PROJECT_STATE_KEY_PREFIX}_${currentProjectId}`;
    }
    return PROJECT_STATE_KEY_PREFIX; // Fallback for new projects
  }, [currentProjectId]);

  // Try to restore state from localStorage on initial mount
  const getInitialState = (key, defaultValue) => {
    try {
      const savedState = localStorage.getItem(PROJECT_STATE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Verify the project ID matches (prevent cross-project contamination)
        if (currentProjectId && parsed.projectId !== currentProjectId) {
          console.log("🗑️ Clearing stale state from different project");
          localStorage.removeItem(PROJECT_STATE_KEY);
          return defaultValue;
        }
        return parsed[key] || defaultValue;
      }
    } catch (error) {
      console.error("Error restoring project state:", error);
    }
    return defaultValue;
  };

  const [projectTypeState, setProjectTypeState] = useState(() =>
    getInitialState("projectTypeState", {
      projectType: "",
      platformName: "",
      goal: "",
      platformList: [],
      goalList: []
    })
  );
  const [projectDetailsState, setProjectDetailState] = useState(() =>
    getInitialState("projectDetailsState", {
      projectName: "",
      projectDescription: "",
      adType: "",
      imageType: "",
      minAge: 18,
      maxAge: 64,
      gender: "male",
      age: "",
      interest: ""
    })
  );
  const [projectDetailsGeneratedData, setProjectDetailsGeneratedData] =
    useState(() => getInitialState("projectDetailsGeneratedData", {}));

  // Check URL on mount to determine if we should restore to template page
  useEffect(() => {
    if (currentProjectId && step !== 4) {
      try {
        const savedState = localStorage.getItem(PROJECT_STATE_KEY);
        if (savedState) {
          const parsed = JSON.parse(savedState);
          // Verify project ID matches before restoring
          if (parsed.savedStep === 4 && parsed.projectId === currentProjectId) {
            console.log(
              `📂 Restoring template page state for project ${currentProjectId}`
            );
            setStep(4);
          }
        }
      } catch (error) {
        console.error("Error checking saved state:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Save state to localStorage whenever it changes and we're on step 4
  useEffect(() => {
    if (step === 4 && currentProjectId) {
      try {
        const stateToSave = {
          projectId: currentProjectId, // Store project ID for verification
          savedStep: 4,
          projectTypeState,
          projectDetailsState,
          projectDetailsGeneratedData,
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(PROJECT_STATE_KEY, JSON.stringify(stateToSave));
        console.log(
          `💾 Saved project wizard state for project ${currentProjectId}`
        );
      } catch (error) {
        console.error("Error saving project state:", error);
      }
    }
  }, [
    step,
    currentProjectId,
    PROJECT_STATE_KEY,
    projectTypeState,
    projectDetailsState,
    projectDetailsGeneratedData
  ]);

  // Clear saved state when leaving the wizard (going back to dashboard)
  useEffect(() => {
    if (step === 1 && !currentProjectId) {
      // Starting a new project (no project ID), clear any lingering state
      // Also clear old global state key for cleanup
      localStorage.removeItem(PROJECT_STATE_KEY);
      localStorage.removeItem(PROJECT_STATE_KEY_PREFIX);
      console.log(
        "🗑️ Cleared saved project wizard state (starting new project)"
      );
    }
  }, [step, currentProjectId, PROJECT_STATE_KEY]);

  // Clear state from other projects on mount
  useEffect(() => {
    // Clean up state from other projects
    const allKeys = Object.keys(localStorage);
    const projectStateKeys = allKeys.filter(
      (key) =>
        key.startsWith(PROJECT_STATE_KEY_PREFIX) && key !== PROJECT_STATE_KEY
    );

    if (projectStateKeys.length > 5) {
      // Keep only the 5 most recent project states
      projectStateKeys.sort((a, b) => {
        try {
          const aState = JSON.parse(localStorage.getItem(a));
          const bState = JSON.parse(localStorage.getItem(b));
          return new Date(bState.savedAt) - new Date(aState.savedAt);
        } catch {
          return 0;
        }
      });

      // Remove older states
      projectStateKeys.slice(5).forEach((key) => {
        localStorage.removeItem(key);
        console.log(`🧹 Cleaned up old project state: ${key}`);
      });
    }
  }, [PROJECT_STATE_KEY]);

  const getComponents = () => {
    switch (step) {
      case 1:
        return (
          <ProjectType
            setImportProject={setImportProject}
            projectTypeState={projectTypeState}
            setProjectTypeState={setProjectTypeState}
            setStep={setStep}
            setProjectButtonVisible={setProjectButtonVisible}
            brandDetails={brandDetails}
          />
        );
      case 2:
        return (
          <ProjectDetails
            setStep={setStep}
            setProjectButtonVisible={setProjectButtonVisible}
            projectTypeState={projectTypeState}
            projectDetailsState={projectDetailsState}
            setProjectDetailState={setProjectDetailState}
            brandDetails={brandDetails}
            projectDetailsGeneratedData={projectDetailsGeneratedData}
            setProjectDetailsGeneratedData={setProjectDetailsGeneratedData}
            projectList={projectList}
          />
        );
      case 3:
        return <FinalReview setStep={setStep} />;
      case 4:
        return (
          <AllTemplatesEnhanced
            setStep={setStep}
            projectDetailsState={projectDetailsState}
            projectDetailsGeneratedData={projectDetailsGeneratedData}
            brandDetails={brandDetails}
          />
        );

      default:
        return null;
    }
  };

  return <div>{getComponents()}</div>;
}
